import BottomSheetContent from '../../../../../../../components/BottomSheetComponents/BottomSheetContent'
import { PrimaryButton } from '../../../../../../../components/execution/PrimaryButton'
import { SecondaryButton } from '../../../../../../../components/execution/SecondaryButton'
import { EventNames } from '../../../../../../../types/misc.types'
import { lightImpactVibrate, trackEvent } from '../../../../../../../utils/appBridge'
import {
  PlansLumpsumRecommendationScreenByPlanMode,
  PlansLumpsumSheetEventIds
} from '../../../../analytics/plansEvents'
import { PlansPlanMode } from '../../../api/types'

type PlanRecommendationSheetErrorStateProps = {
  planMode: PlansPlanMode.REBALANCE | PlansPlanMode.ALLOCATION | PlansPlanMode.IDEAL_PORTFOLIO
  onRetry: () => void
  onDismiss: () => void
  /** Overrides default “Something went wrong” heading */
  heading?: string
  /** Overrides the lead body paragraph; supporting paragraphs still follow */
  description?: string
  analyticsScreen?: string
  fundName?: string
  fundIsin?: string
}

const PLAN_RECOMMENDATION_ERROR_LEAD_FOR_FUND: Record<
  PlanRecommendationSheetErrorStateProps['planMode'],
  (fundLabel: string) => string
> = {
  [PlansPlanMode.REBALANCE]: (fundLabel) =>
    `We couldn't load the rebalance recommendation for ${fundLabel}.`,
  [PlansPlanMode.ALLOCATION]: (fundLabel) =>
    `We couldn't load the allocation recommendation for ${fundLabel}.`,
  [PlansPlanMode.IDEAL_PORTFOLIO]: (fundLabel) =>
    `We couldn't load the recommendation for ${fundLabel}.`
}

const PLAN_RECOMMENDATION_ERROR_BODY = [
  "The recommendation details for this fund didn’t come through. This is usually temporary — a slow connection or a brief delay fetching that fund’s plan.",
  'Tap Retry to load this fund’s recommendation again, or Close and open it later.'
]

const PLAN_RECOMMENDATION_ERROR_ACCENT: Record<
  PlanRecommendationSheetErrorStateProps['planMode'],
  string
> = {
  [PlansPlanMode.REBALANCE]: '#cc5c2d',
  [PlansPlanMode.ALLOCATION]: '#7048AC',
  [PlansPlanMode.IDEAL_PORTFOLIO]: '#2e8ae5'
}

/**
 * Contextual load/unavailable error for plan recommendation sheets.
 * Uses BottomSheetContent logo + heading + stacked Retry / Close CTAs.
 */
export function PlanRecommendationSheetErrorState({
  planMode,
  onRetry,
  onDismiss,
  heading = 'Something went wrong',
  description,
  analyticsScreen,
  fundName = '',
  fundIsin = ''
}: PlanRecommendationSheetErrorStateProps) {
  const screenName = analyticsScreen ?? PlansLumpsumRecommendationScreenByPlanMode?.[planMode]
  const fundLabel = fundName.trim().length > 0 ? fundName.trim() : 'this fund'
  const bodyParagraphs = [
    description ?? PLAN_RECOMMENDATION_ERROR_LEAD_FOR_FUND[planMode](fundLabel),
    ...PLAN_RECOMMENDATION_ERROR_BODY
  ]

  function handleRetry() {
    lightImpactVibrate()
    trackEvent({
      eventName: EventNames.ButtonTap,
      payload: {
        ScreenName: screenName,
        eventId: PlansLumpsumSheetEventIds.Retry,
        buttonLabel: 'Retry',
        ...(fundName ? { fundName } : {}),
        ...(fundIsin ? { fundIsin } : {})
      }
    })
    onRetry()
  }

  function handleClose() {
    lightImpactVibrate()
    trackEvent({
      eventName: EventNames.UserClicked,
      payload: {
        ScreenName: screenName,
        eventId: PlansLumpsumSheetEventIds.Close,
        buttonLabel: 'Close',
        ...(fundName ? { fundName } : {}),
        ...(fundIsin ? { fundIsin } : {})
      }
    })
    onDismiss()
  }

  return (
    <BottomSheetContent
      onDismiss={handleClose}
      logoSvg={<PlanRecommendationSheetErrorLogo planMode={planMode} />}
      heading={heading}
      bottomSheetHeadingClassName="!mb-2 !font-[500] !leading-[130%]"
      bottomSheetHeadClassName="!pt-6"
      bottomSheetBodyClassName="!pb-2"
      bottomSheetCtaWrapperClassName="z-10"
      bottomSheetCtas={
        <div className="flex w-full flex-col gap-3">
          <PrimaryButton
            label="Retry"
            onClick={handleRetry}
            primaryButtonClassName="!h-[56px] !w-full !text-[16px]"
          />
          <SecondaryButton
            label="Close"
            onClick={handleClose}
            secondaryButtonClassName="!h-[56px] !w-full !text-[16px]"
          />
        </div>
      }
    >
      <div className="flex flex-col gap-3 text-[14px] font-[400] leading-[150%] text-[#6A6E6E]">
        {bodyParagraphs.map((paragraph) => (
          <p key={paragraph}>{paragraph}</p>
        ))}
      </div>
    </BottomSheetContent>
  )
}

/** Left-aligned alert mark — fills the viewBox with no empty padding. */
function PlanRecommendationSheetErrorLogo({
  planMode
}: {
  planMode: PlanRecommendationSheetErrorStateProps['planMode']
}) {
  const accent = PLAN_RECOMMENDATION_ERROR_ACCENT[planMode]

  return (
    <svg
      viewBox="0 0 56 56"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
      className="block size-14 shrink-0"
    >
      <rect width="56" height="56" rx="14" fill={accent} fillOpacity="0.12" />
      <circle cx="28" cy="28" r="16" stroke={accent} strokeWidth="2.75" />
      <path d="M28 18v12" stroke={accent} strokeWidth="3" strokeLinecap="round" />
      <circle cx="28" cy="36.5" r="2.25" fill={accent} />
    </svg>
  )
}
